import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  IconButton,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';

import React, { useEffect, useState } from 'react';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MdEdit } from 'react-icons/md';
import OptionsTreatmentAreaModal from './components/OptionsTreatmentAreaModal';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { logoutUser } from 'utils';
import { createAPIEndPoint } from 'config/api/api';
import Loader from 'loaders/Loader';
import ErrorMessage from 'components/error-message';

const columnHelper = createColumnHelper();
export default function TreatmentAreaDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  // const serviceId = location.state.serviceId || {};
  const pathSegments = location.pathname.split('/');

  // Get the last segment
  const serviceId =
    location.state?.serviceId || pathSegments[pathSegments.length - 1];
  // const treatment_area_id = location.state.treatmentAreaId || {};
  console.log(' TreatmentAreaDetails ~ sssssss:', serviceId);

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [type, setType] = useState('add');
  const [selectedTreatmentAreas, setSelectedTreatmentAreas] = useState([]);
  const [treatmentAreas, setTreatmentAreas] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);

  const fetchTreatmentOptions = async () => {
    setLoading(true);
    try {
      const response = await createAPIEndPoint(
        // `service/${serviceId}/treatment_options`,
        `get_treatment_options/${serviceId}`,
      ).fetchAll();
      setTreatmentAreas(response.data.data || []);

      console.log(response.data.treatment_options || []);
    } catch (err) {
      setLoading(false);
      toast.error(
        err?.response?.data?.error || 'Error fetching treatment area',
      );
      if (err?.response?.data?.error?.includes('Invalid Bearer token')) {
        logoutUser(navigate);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTreatmentOptions();
  }, []);

  // Options Table Columns
  const treatmentOptionsColumns = [
    columnHelper.accessor('option_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Option Name
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('option_type', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          option Type
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          STATUS
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          active: 'green',
          inactive: 'red',
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="72px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleEditClick = (e) => {
          onOpen();
          setType('edit');
          console.log('Edit Modal');
          setSelectedTreatmentAreas(info.row.original);
        };

        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
          </Box>
        );
      },
    }),
  ];

  const treatmentOptions = useReactTable({
    data: treatmentAreas,
    columns: treatmentOptionsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      {loading && <Loader />}
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <BackButton />
        {/* Main Fields */}
        <Grid
          templateColumns={{
            base: '1fr',
            lg: '1fr',
          }}
          templateRows={{
            base: 'repeat(2, 1fr)',
            lg: '1fr',
          }}
          gap={{ base: '20px', xl: '20px' }}
        >
          <Card mb={{ base: '0px', '2xl': '20px' }}>
            <Flex
              px={{ base: '16px', md: '25px' }}
              mb="8px"
              justify="space-between"
              align="center"
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 4, md: 0 }}
            >
              <Text
                color={textColor}
                fontSize="24px"
                fontWeight="700"
                lineHeight="100%"
                mb={{ base: '8px', md: '0px' }}
              >
                Manage Treatment Options
              </Text>

              <Box w={{ base: '100%', md: 'auto' }}>
                <Button
                  w={{ base: '100%', md: 'fit-content' }}
                  mr={{ base: '0', md: '8px' }}
                  minW="140px"
                  variant="brand"
                  fontWeight="500"
                  onClick={() => {
                    setType('add'); // Always set it to 'add' without the condition
                    setSelectedTreatmentAreas([]);
                    onOpen();
                  }}
                >
                  Add Option
                </Button>
              </Box>
            </Flex>
            <Table variant="striped" color="gray.500" mb="24px" mt="12px">
              <Thead>
                {treatmentOptions.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                      >
                        <Flex justifyContent="space-between" align="center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {treatmentOptions.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {!loading && treatmentAreas.length === 0 && (
              <ErrorMessage source="insurance" />
            )}
          </Card>
        </Grid>
      </Box>
      <OptionsTreatmentAreaModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        initialData={selectedTreatmentAreas}
        treatment_area_id={serviceId}
        fetchAgain={fetchTreatmentOptions}
      />
    </>
  );
}
